/*
 * DEINSPAR – Collection return v2.0
 * Restores the items shown with "Mehr zeigen" and the clicked card's position
 * when Safari reloads a collection while navigating back from a product.
 *
 * Safe to include on all collection templates. Does not modify theme.js,
 * collection.js, Shopify variants or the native load-more click handler.
 */
(() => {
  'use strict';

  const PREFIX = 'deinspar:collection-return:v2:';
  const HISTORY_KEY = '__deinsparCollectionReturnV2';
  const MAX_AGE = 45 * 60 * 1000;
  const MAX_PAGES = 130;
  const getGrid = () => document.querySelector('.sec__collection-main .loadmore-lists');
  const getButton = () => document.querySelector('.sec__collection-main loadmore-button .actions-loadmore');
  const getBar = () => document.querySelector('.sec__collection-main loadmore-button .load-more-bar');
  const cards = grid => Array.from(grid?.children || []).filter(el => el.classList.contains('loadmore-item'));
  const pageKey = () => location.pathname + location.search;
  const storeKey = () => PREFIX + pageKey();

  function hrefToUrl(href) {
    if (!href) return null;
    try {
      const url = new URL(href, location.href);
      return url.origin === location.origin && url.pathname === location.pathname &&
        Number(url.searchParams.get('page')) > 0 ? url : null;
    } catch (_) { return null; }
  }

  function readState() {
    let stored = null;
    try { stored = JSON.parse(sessionStorage.getItem(storeKey()) || 'null'); } catch (_) {}
    const fromHistory = history.state && typeof history.state === 'object'
      ? history.state[HISTORY_KEY] : null;
    const candidates = [stored, fromHistory].filter(state => state &&
      state.key === pageKey() && Number.isFinite(state.time) &&
      Date.now() - state.time >= 0 && Date.now() - state.time <= MAX_AGE);
    candidates.sort((a, b) => b.time - a.time);
    return candidates[0] || null;
  }

  function remember(state) {
    try { sessionStorage.setItem(storeKey(), JSON.stringify(state)); } catch (_) {}
    // History entries survive a real browser Back even if iOS purges the page.
    // Merge with Shopify's history.state instead of replacing other fields.
    try {
      const existing = history.state && typeof history.state === 'object' ? history.state : {};
      history.replaceState({ ...existing, [HISTORY_KEY]: state }, '', location.href);
    } catch (_) {}
  }

  function start() {
    let grid = getGrid();
    if (!grid || !getBar()) return;

    let firstNext = getButton()?.getAttribute('href') || null;
    let initialCount = cards(grid).length;
    let knownCount = initialCount;
    let rememberedIndex = null;
    let rememberedTop = null;
    let navigationPending = false;
    let restoring = false;
    let observer = null;
    let restoreAttempted = false;
    let lastClickedPageUrl = null;

    function currentState() {
      return {
        key: pageKey(),
        time: Date.now(),
        initialCount,
        count: cards(grid).length,
        firstNext,
        next: getButton()?.getAttribute('href') || null,
        nextRemoved: !getButton(),
        bar: getBar()?.innerHTML || '',
        index: rememberedIndex,
        top: rememberedTop,
        scrollY: Math.max(0, window.scrollY || 0),
        pending: navigationPending,
        product: lastClickedPageUrl
      };
    }

    function save() {
      if (restoring || getGrid() !== grid) return;
      remember(currentState());
    }

    function observe() {
      observer?.disconnect();
      observer = new MutationObserver(() => {
        if (restoring) return;
        const freshGrid = getGrid();
        if (freshGrid !== grid) {
          // Sorting and filtering may replace the entire result grid.
          if (!freshGrid) return;
          grid = freshGrid;
          firstNext = getButton()?.getAttribute('href') || null;
          initialCount = cards(grid).length;
          knownCount = initialCount;
          rememberedIndex = null;
          rememberedTop = null;
          navigationPending = false;
          observe();
          return;
        }
        const count = cards(grid).length;
        if (count > knownCount) {
          knownCount = count;
          // Shopify has already updated the next link synchronously after
          // appending the cards. A compact state is safe even for 1000+ items.
          save();
        }
      });
      observer.observe(grid, { childList: true });
    }

    function requiredUrls(state) {
      const first = hrefToUrl(state.firstNext);
      const next = hrefToUrl(state.next);
      if (!first) return [];
      const start = Number(first.searchParams.get('page'));
      // When the last page was loaded Shopify removes the button. Estimate
      // its page number from the original page size, including a short last page.
      const last = next
        ? Number(next.searchParams.get('page')) - 1
        : start - 1 + Math.ceil(Math.max(0, state.count - state.initialCount) /
            Math.max(1, state.initialCount));
      if (!Number.isInteger(start) || !Number.isInteger(last) || last < start ||
          last - start + 1 > MAX_PAGES) return [];
      return Array.from({ length: last - start + 1 }, (_, i) => {
        const url = new URL(first.href);
        url.searchParams.set('page', String(start + i));
        return url;
      });
    }

    async function loadPage(url) {
      if (url.origin !== location.origin || url.pathname !== location.pathname) {
        throw new Error('Unexpected collection URL');
      }
      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Shopify HTTP ' + response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nodes = Array.from(doc.querySelectorAll('.sec__collection-main .loadmore-lists .loadmore-item'));
      if (!nodes.length) throw new Error('No products found in restored page');
      return {
        nodes,
        next: doc.querySelector('.sec__collection-main .actions-loadmore')?.getAttribute('href') || null,
        bar: doc.querySelector('.sec__collection-main .load-more-bar')?.innerHTML || ''
      };
    }

    function updateButton(nextHref) {
      const button = getButton();
      if (button) {
        if (nextHref) button.setAttribute('href', nextHref);
        else button.remove();
      }
    }

    function scrollBack(state) {
      const target = Number.isInteger(state.index) ? cards(grid)[state.index] : null;
      const savedY = Number(state.scrollY) || 0;
      const savedTop = Number(state.top);
      let userMoved = false;
      const stop = () => { userMoved = true; };
      ['touchstart', 'pointerdown', 'wheel', 'keydown'].forEach(type =>
        window.addEventListener(type, stop, { passive: true, once: true })
      );
      const position = () => {
        if (userMoved || getGrid() !== grid) return;
        const y = target && Number.isFinite(savedTop)
          ? window.scrollY + target.getBoundingClientRect().top - savedTop
          : savedY;
        window.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
      };
      requestAnimationFrame(() => requestAnimationFrame(position));
      [120, 350, 750, 1400, 2300].forEach(ms => setTimeout(position, ms));
    }

    async function restore(state) {
      if (restoring || restoreAttempted || getGrid() !== grid) return;
      restoreAttempted = true;
      if (!state || state.count <= initialCount || !state.firstNext) return;
      const urls = requiredUrls(state);
      if (!urls.length) {
        console.warn('[Deinspar] Keine gültigen Produktseiten für die Wiederherstellung gefunden.');
        return;
      }
      restoring = true;
      const button = getButton();
      if (button) {
        button.classList.add('loading');
        button.style.pointerEvents = 'none';
        button.setAttribute('aria-busy', 'true');
      }
      let restoredFully = false;
      try {
        // A limited fetch concurrency avoids flooding Shopify or mobile Safari.
        // Every batch is appended in the original collection order.
        for (let i = 0; i < urls.length; i += 4) {
          const pages = await Promise.all(urls.slice(i, i + 4).map(loadPage));
          if (getGrid() !== grid) throw new Error('Collection changed during restore');
          pages.forEach(page => {
            grid.append(...page.nodes);
            if (page.bar && getBar()) getBar().innerHTML = page.bar;
            updateButton(page.next);
          });
        }
        restoredFully = cards(grid).length >= state.count;
        if (!restoredFully) throw new Error('Expected ' + state.count + ' cards, got ' + cards(grid).length);
        if (state.bar && getBar()) getBar().innerHTML = state.bar;
        updateButton(state.nextRemoved ? null : state.next);
        knownCount = cards(grid).length;
        rememberedIndex = state.index;
        rememberedTop = state.top;
        navigationPending = false;
        lastClickedPageUrl = state.product || null;
        // Scroll only after all original cards exist in the DOM.
        scrollBack(state);
      } catch (error) {
        console.warn('[Deinspar] Wiederherstellung war nicht vollständig:', error);
      } finally {
        restoring = false;
        if (button?.isConnected) {
          button.classList.remove('loading');
          button.style.removeProperty('pointer-events');
          button.removeAttribute('aria-busy');
        }
        // Never overwrite a valid complete snapshot with the initial page
        // when a network error interrupted restoration. Keep it for a retry.
        if (restoredFully) save();
      }
    }

    observe();

    // Shopify filter/sort AJAX can replace the entire grid without firing
    // mutations on the old grid. Rebind when the new grid enters the DOM.
    const replacementObserver = new MutationObserver(records => {
      if (restoring) return;
      const gridWasInserted = records.some(record =>
        Array.from(record.addedNodes).some(node => node.nodeType === 1 && (
          node.matches?.('.loadmore-lists, .sec__collection-main, #productgridcontainer') ||
          node.querySelector?.('.loadmore-lists')
        ))
      );
      if (gridWasInserted && getGrid() && getGrid() !== grid) {
        grid = getGrid();
        firstNext = getButton()?.getAttribute('href') || null;
        initialCount = cards(grid).length;
        knownCount = initialCount;
        rememberedIndex = null;
        rememberedTop = null;
        navigationPending = false;
        observe();
      }
    });
    replacementObserver.observe(document.body, { childList: true, subtree: true });

    // Capture a normal product navigation before leaving the collection.
    document.addEventListener('click', event => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey ||
          event.metaKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.target === '_blank' || !grid.contains(link)) return;
      let url;
      try { url = new URL(link.href, location.href); } catch (_) { return; }
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
      const clickedCard = link.closest('.loadmore-item');
      rememberedIndex = clickedCard ? cards(grid).indexOf(clickedCard) : null;
      rememberedTop = clickedCard ? clickedCard.getBoundingClientRect().top : null;
      lastClickedPageUrl = url.pathname;
      navigationPending = true;
      save();
    }, true);

    // Only use pagehide/visibilitychange as a secondary snapshot. The main
    // click listener already saves a tiny, complete state synchronously.
    window.addEventListener('pagehide', () => { if (navigationPending) save(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && navigationPending) save();
    });

    window.addEventListener('pageshow', event => {
      if (event.persisted) {
        // BFCache kept all the cards and their native scroll position.
        navigationPending = false;
        save();
      }
    });

    const saved = readState();
    // `pending` can be cleared by iOS lifecycle ordering after a cached back;
    // the browser navigation type gives us an independent signal on reload.
    const isHistoryBack = performance.getEntriesByType('navigation')
      .some(entry => entry.type === 'back_forward');
    if (saved && (saved.pending || isHistoryBack)) restore(saved);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else start();
})();
